import { useCallback } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { usersQueries } from '@repo/shared-kit/api/current-user';
import { KycRouter } from '@repo/identity-verification-kit/router';
import { useKycFlowSpan } from '@repo/identity-verification-kit/tracking/use-kyc-flow-span';
import type { KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { useSuspenseRegistration } from 'register-partners-js/react/hooks/use-registration';
import { useSubmitRegistration } from 'register-partners-js/react/hooks/use-submit-registration';
import { KycLoading } from './loading';

export function KycRoute() {
  const { registration_id: registrationId } = useParams<{
    registration_id: string;
  }>();
  const {
    data: { organization, owner },
  } = useSuspenseRegistration(registrationId);
  const { data: currentUserData } = useQuery(usersQueries.getMe());

  const { submitAsync, isSubmitting, isContractSignPending } = useSubmitRegistration({
    registrationId: registrationId ?? '',
    organizationId: organization.id,
  });

  const { markSuccess, markError } = useKycFlowSpan({
    membershipId: owner?.id,
    userId: currentUserData?.user?.id,
  });

  const handleComplete = useCallback(
    async (provider: keyof typeof KycProvider, outcome: 'completed' | 'skipped' = 'completed') => {
      markSuccess(provider, outcome);
      await submitAsync();
    },
    [submitAsync, markSuccess]
  );

  if (!organization.legalCountry) {
    return null;
  }

  if (isSubmitting || isContractSignPending) {
    return <KycLoading />;
  }

  return (
    <KycRouter
      // FIXME: Will add provider & provider details later
      provider={null}
      providerDetails={null}
      hasGermanResidencePermit={false}
      isRegisterFlow
      legalCountry={organization.legalCountry}
      membershipId={owner?.id}
      onComplete={handleComplete}
      onError={markError}
      trackingPrefix="onboarding_partner"
      userId={currentUserData?.user?.id}
    />
  );
}
